import React, { useMemo } from "react";
import {
  Box,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  ChevronLeft,
  ChevronRight,
  Event,
  FlightTakeoff,
  Lock,
  SentimentVeryDissatisfied,
} from "@material-ui/icons";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfMonth,
} from "date-fns";
import {
  holidayCoversLocalDay,
  formatLeaveDays,
  formatDurationMins,
  recordOverlapsLocalRange,
  summarizeUserHolidaysForYear,
} from "../utils/holidayDates";

const BOOKED_COLOR = "#1565c0";
const SICKNESS_COLOR = "#c2185b";
const OTHER_COLOR = "#ef6c00";
const PUBLIC_HOLIDAY_COLOR = "#2e7d32";

function formatHolidayDurationTooltip(holiday) {
  if (holiday?.durationType === "PartDay") {
    return holiday.partDayType
      ? `Part Day (${holiday.partDayType})`
      : "Part Day";
  }
  return "Whole Day";
}

function formatOtherTooltip(record) {
  const type = record.eventType || "Other";
  const duration =
    record.durationMins != null
      ? ` · ${formatDurationMins(record.durationMins)}`
      : "";
  return `${type}${duration}`;
}

function publicHolidayTooltipForDay(day, publicHolidays) {
  const dayPublic = (publicHolidays || []).filter((p) =>
    holidayCoversLocalDay(p, day)
  );
  if (!dayPublic.length) return null;
  return dayPublic.map((p) => p.title || "Public Holiday").join(", ");
}

export default function HolidayMonthView({
  detailsUser,
  holidays = [],
  sicknesses = [],
  others = [],
  publicHolidays = [],
  month,
  onMonthChange,
}) {
  const classes = useStyles();

  const days = useMemo(() => {
    if (!month) return [];
    return eachDayOfInterval({
      start: startOfMonth(month),
      end: endOfMonth(month),
    });
  }, [month]);

  const userHolidays = useMemo(() => {
    if (!detailsUser?.Id) return [];
    return (holidays || []).filter(
      (h) => String(h.appUserId) === String(detailsUser.Id)
    );
  }, [detailsUser, holidays]);

  const userSicknesses = useMemo(() => {
    if (!detailsUser?.Id) return [];
    return (sicknesses || []).filter(
      (s) => String(s.appUserId) === String(detailsUser.Id)
    );
  }, [detailsUser, sicknesses]);

  const userOthers = useMemo(() => {
    if (!detailsUser?.Id) return [];
    return (others || []).filter(
      (o) => String(o.appUserId) === String(detailsUser.Id)
    );
  }, [detailsUser, others]);

  const monthPublicHolidays = useMemo(() => {
    if (!month) return [];
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    return (publicHolidays || []).filter((p) =>
      recordOverlapsLocalRange(p, monthStart, monthEnd)
    );
  }, [publicHolidays, month]);

  const monthTotals = useMemo(() => {
    if (!detailsUser?.Id || !month) {
      return { holidayDays: 0, sicknessDays: 0, otherMins: 0 };
    }
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const holidaySummary = summarizeUserHolidaysForYear(
      holidays,
      detailsUser.Id,
      year,
      28
    );
    const sicknessSummary = summarizeUserHolidaysForYear(
      sicknesses,
      detailsUser.Id,
      year,
      null
    );
    const otherMins = userOthers
      .filter((o) => recordOverlapsLocalRange(o, monthStart, monthEnd))
      .reduce((sum, o) => sum + (o.durationMins || 0), 0);
    return {
      holidayDays: holidaySummary.monthly[monthIndex] || 0,
      sicknessDays: sicknessSummary.monthly[monthIndex] || 0,
      otherMins,
    };
  }, [detailsUser, holidays, sicknesses, userOthers, month]);

  if (!detailsUser) {
    return (
      <Box className={classes.root}>
        <Typography variant="body1" color="textSecondary">
          No user selected
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.monthNav}>
        <IconButton
          size="small"
          onClick={() => onMonthChange(addMonths(month, -1))}
          aria-label="Previous month"
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" className={classes.monthTitle}>
          {format(month, "MMMM yyyy")}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onMonthChange(addMonths(month, 1))}
          aria-label="Next month"
        >
          <ChevronRight />
        </IconButton>
        <Box className={classes.monthTotals}>
          <Box className={classes.totalItem}>
            <Typography variant="body1" className={classes.totalValue}>
              {formatLeaveDays(monthTotals.holidayDays)}
            </Typography>
            <FlightTakeoff className={classes.holidayIcon} />
          </Box>
          <Box className={classes.totalItem}>
            <Typography variant="body1" className={classes.totalValue}>
              {formatLeaveDays(monthTotals.sicknessDays)}
            </Typography>
            <SentimentVeryDissatisfied className={classes.sicknessIcon} />
          </Box>
          <Box className={classes.totalItem}>
            <Typography variant="body1" className={classes.totalValue}>
              {formatDurationMins(monthTotals.otherMins)}
            </Typography>
            <Event className={classes.otherIcon} />
          </Box>
        </Box>
      </Box>

      <Paper className={classes.gridCard} elevation={1}>
        <Box className={classes.gridScroll}>
          <Box
            className={classes.grid}
            style={{
              gridTemplateColumns: `120px repeat(${days.length}, minmax(36px, 1fr))`,
            }}
          >
            <Box className={`${classes.cell} ${classes.cornerCell}`} />
            {days.map((day) => (
              <Box
                key={`day-${format(day, "yyyy-MM-dd")}`}
                className={`${classes.cell} ${classes.headerCell}`}
              >
                <Typography variant="body2" className={classes.dayNumber}>
                  {format(day, "d")}
                </Typography>
              </Box>
            ))}

            <Box className={`${classes.cell} ${classes.labelCell}`}>
              <Typography variant="body1" className={classes.rowLabel}>
                Holiday
              </Typography>
            </Box>
            {days.map((day) => {
              const publicTooltip = publicHolidayTooltipForDay(
                day,
                monthPublicHolidays
              );
              const dayHolidays = userHolidays.filter((h) =>
                holidayCoversLocalDay(h, day)
              );
              const holidayTooltip = dayHolidays
                .map(formatHolidayDurationTooltip)
                .join(", ");
              return (
                <Box
                  key={`holiday-${format(day, "yyyy-MM-dd")}`}
                  className={`${classes.cell} ${classes.dataCell}`}
                >
                  {publicTooltip ? (
                    <Tooltip title={publicTooltip} arrow>
                      <span className={classes.iconWrap}>
                        <Lock className={classes.publicHolidayIcon} />
                      </span>
                    </Tooltip>
                  ) : dayHolidays.length ? (
                    <Tooltip title={holidayTooltip} arrow>
                      <span className={classes.iconWrap}>
                        <FlightTakeoff className={classes.holidayIcon} />
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            })}

            <Box className={`${classes.cell} ${classes.labelCell}`}>
              <Typography variant="body1" className={classes.rowLabel}>
                Sickness
              </Typography>
            </Box>
            {days.map((day) => {
              const publicTooltip = publicHolidayTooltipForDay(
                day,
                monthPublicHolidays
              );
              const onSick = userSicknesses.some((s) =>
                holidayCoversLocalDay(s, day)
              );
              return (
                <Box
                  key={`sickness-${format(day, "yyyy-MM-dd")}`}
                  className={`${classes.cell} ${classes.dataCell}`}
                >
                  {publicTooltip ? (
                    <Tooltip title={publicTooltip} arrow>
                      <span className={classes.iconWrap}>
                        <Lock className={classes.publicHolidayIcon} />
                      </span>
                    </Tooltip>
                  ) : onSick ? (
                    <SentimentVeryDissatisfied className={classes.sicknessIcon} />
                  ) : null}
                </Box>
              );
            })}

            <Box className={`${classes.cell} ${classes.labelCell}`}>
              <Typography variant="body1" className={classes.rowLabel}>
                Other
              </Typography>
            </Box>
            {days.map((day) => {
              const dayOthers = userOthers.filter((o) =>
                holidayCoversLocalDay(o, day)
              );
              const tooltip = dayOthers.map(formatOtherTooltip).join(", ");
              return (
                <Box
                  key={`other-${format(day, "yyyy-MM-dd")}`}
                  className={`${classes.cell} ${classes.dataCell}`}
                >
                  {dayOthers.length ? (
                    <Tooltip title={tooltip} arrow>
                      <span className={classes.iconWrap}>
                        <Event className={classes.otherIcon} />
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  monthNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  monthTitle: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
    minWidth: 180,
    textAlign: "center",
  },
  monthTotals: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
    marginLeft: theme.spacing(2),
  },
  totalItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  totalValue: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  gridCard: {
    overflow: "hidden",
  },
  gridScroll: {
    overflowX: "auto",
  },
  grid: {
    display: "grid",
    width: "100%",
    minWidth: "fit-content",
  },
  cell: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 48,
    borderBottom: "1px solid #e0e0e0",
    borderRight: "1px solid #f0f0f0",
    boxSizing: "border-box",
  },
  cornerCell: {
    background:
      "linear-gradient(90deg, rgba(245,245,245,1) 0%, rgba(255,255,255,1) 100%)",
    borderRight: "1px solid #e0e0e0",
  },
  headerCell: {
    background:
      "linear-gradient(180deg, rgba(245,245,245,1) 0%, rgba(255,255,255,1) 100%)",
    minHeight: 40,
  },
  labelCell: {
    justifyContent: "flex-start",
    paddingLeft: theme.spacing(2),
    background:
      "linear-gradient(90deg, rgba(245,245,245,1) 0%, rgba(255,255,255,1) 100%)",
    borderRight: "1px solid #e0e0e0",
  },
  dataCell: {
    backgroundColor: "#fff",
  },
  dayNumber: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  rowLabel: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
  },
  holidayIcon: {
    color: BOOKED_COLOR,
    fontSize: 20,
  },
  sicknessIcon: {
    color: SICKNESS_COLOR,
    fontSize: 20,
  },
  otherIcon: {
    color: OTHER_COLOR,
    fontSize: 20,
  },
  publicHolidayIcon: {
    color: PUBLIC_HOLIDAY_COLOR,
    fontSize: 20,
  },
  iconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 0,
  },
}));
